<template>
  <ValidationObserver
    ref="buttonsObserver"
    :class="{ center }"
    tag="div" >
    <Title
      :title="title"
      :subtitle="subtitle"
      :subtitle-prefix-icon="subtitlePrefixIcon"
      :type="titleType" />
    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="button-fields" >
      <ValidationProvider
        v-slot="{ errors, failedRules }"
        :name="`Button ${index + 1}`"
        :rules="proccessedValidators" >
        <b-form-group>
          <div class="option">
            <input
              :id="`buttonInput-${index}`"
              :ref="`buttonInputRef-${index}`"
              v-model="button.value"
              :maxlength="maxTextPreventInput ? buttonMaxTextLength : null"
              type="text"
              class="w-100 sb2-bordered option-item mb-0"
              :class="{ 'sb2-active sb2-hoverable sb2-focusable': !button.readOnly, empty: !button.value }"
              :placeholder="placeholder"
              :readonly="button.readOnly"
              @keyup.enter="enterPressed" >
            <SBIcon
              v-if="!button.readOnly && showRemoveButton"
              class="mr-2 delete"
              :icon="SBIconList.CLOSE_THIN"
              :size="10"
              :hover-size="20"
              :color="SBColors.greyPrimary"
              :background-color="SBColors.white"
              :hover-icon-color="SBColors.blackPrimary"
              @click="removeOption(index)" />
          </div>
          <div
            v-if="errors[0]"
            class="error flex"
            :class="[center ? 'center-all' : 'flex-align-center', { warning: warningValidatorsList.includes(Object.keys(failedRules)[0])}]">
            <SBIcon
              class="mr-1"
              :icon="warningValidatorsList.includes(Object.keys(failedRules)[0]) ? SBIconList.INFO : SBIconList.ERROR"
              :color="warningValidatorsList.includes(Object.keys(failedRules)[0]) ? '#DEBD30' : SBColors.redPrimary"
              :size="10" />
            <span>{{ validatorMessagesProcessed && validatorMessagesProcessed[Object.keys(failedRules)[0]] ? validatorMessagesProcessed[Object.keys(failedRules)[0]] : errors[0] }}</span>
          </div>
        </b-form-group>
      </ValidationProvider>
      <div
        v-if="button.subtitle"
        class="subtitle flex flex-align-center mb-0">
        <img
          v-if="button.subtitlePrefixIcon && typeof button.subtitlePrefixIcon === 'string'"
          :src="button.subtitlePrefixIcon"
          style="width: 10px; height: 10px;"
          class="mr-1"
        >
        <SBIcon
          v-else-if="button.subtitlePrefixIcon"
          class="mr-1"
          :icon="button.subtitlePrefixIcon"
          :color="SBColors.greyLight"
          :size="10" />
        <span>{{ button.subtitle }}</span>
      </div>
    </div>
    <span
      v-if="!noAddButton && (!maxButtons || (maxButtons && buttons.length < maxButtons))"
      class="add-more-btn"
      :class="{
        disabled: !valid || hasEmptyButtons,
        black: blackButtons,
      }"
      @click="addNewButtons">
      <span class="flex flex-align-center">
        <svg
          v-if="addButtonIcon && typeof addButtonIcon === 'string' && addButtonIcon.endsWith('.svg')"
          class="mr-1 flex-start-image"
          :data-src="addButtonIcon"
          fill="currentColor"
          width="10"
          height="10" />
        <img
          v-else-if="addButtonIcon && typeof addButtonIcon === 'string'"
          :src="addButtonIcon"
          style="width: 8px; height: 8px;"
          class="mr-1" >
        <SBIcon
          v-else-if="!noSubtitlePrefixIcon"
          class="mr-2"
          :icon="addButtonIcon || SBIconList.PLUS"
          :color="blackButtons ? SBColors.blackPrimary : SBColors.bluePrimary"
          :size="8" />
        <span>{{ addButtonText }}</span>
      </span>
    </span>
  </ValidationObserver>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import Title from '@/components/formElements/Title.form';

export default {
  name: 'ButtonList',
  components: {
    Title,
    SBIcon,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    titleType: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Enter button text',
    },
    validators: {
      type: String,
      default: '',
    },
    externalUniques: {
      type: Array,
      default: () => ([]),
    },
    center: {
      type: Boolean,
      default: false,
    },
    validatorMessages: {
      type: Object,
      default: () => ({}),
    },
    maxTextPreventInput: {
      type: Boolean,
      default: false,
    },
    noAddButton: {
      type: Boolean,
      default: false,
    },
    minButtons: {
      type: Number,
      default: 1,
    },
    maxButtons: {
      type: Number,
      default: null,
    },
    blackButtons: {
      type: Boolean,
      default: false,
    },
    addButtonText: {
      type: String,
      default: 'Add',
    },
    addButtonIcon: {
      type: String,
      default: '',
    },
    warningValidators: {
      type: String,
      default: '',
    },
    enter: {
      type: String,
      default: '',
    },
    subtitle: {
      type: [String, null],
      required: false,
      default: null,
    },
    subtitlePrefixIcon: {
      type: [String, null],
      required: false,
      default: null,
    },
    noSubtitlePrefixIcon: {
      type: Boolean,
      default: false,
    },
    info: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      initialized: false,
      buttons: [],
      valid: true,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    buttonValuesArray() {
      return [
        'validator has an issue if only one value is provided so this one is a dummy so we always have a minimum of two for the validator',
        ...this.buttons.map(button => button.value),
      ];
    },
    buttonUniqueValuesArray() {
      return [
        'validator has an issue if only one value is provided so this one is a dummy so we always have a minimum of two for the validator',
        ...(this.externalUniques ? this.externalUniques.map(button => button) : []),
      ];
    },
    buttonMaxTextLength() {
      return this.validatorsKeyValue.maxLength || null;
    },
    showRemoveButton() {
      return this.buttons.length > this.minButtons;
    },
    warningValidatorsList() {
      return this.warningValidators ? this.warningValidators.split(',') : [];
    },
    validatorsKeyValue() {
      const v = {};
      if (this.validators) {
        this.validators.split('|').forEach((validator) => {
          const [key, value] = validator.split(':');
          v[key] = value;
        });
      }
      return v;
    },
    /**
     * Validator messages can hold placeholders to show the value inside a message.
     * Maybe not very usefull for basic validators, but can be very usefull for custom validators and some dynamic data.
     */
    validatorMessagesProcessed() {
      const messages = {};
      if (this.validatorsKeyValue) {
        Object.keys(this.validatorMessages).forEach((key) => {
          messages[key] = this.validatorMessages[key].replace(/{{validator}}/g, this.validatorsKeyValue[key]);
        });
      }
      return messages;
    },
    proccessedValidators() {
      let validators = this.validators ? this.validators.replace('uniques', `uniques:${this.buttonValuesArray}`) : '';
      validators = validators.replace(/externalUniques/g, `externalUniques:${this.buttonUniqueValuesArray}`);
      return validators;
    },
    hasEmptyButtons() {
      return this.buttons.filter(button => !button.value).length;
    },
  },
  watch: {
    buttons: {
      handler() {
        this.emitData();
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setValue();
    if (this.buttons.length < this.minButtons) {
      for (let i = this.buttons.length; i < this.minButtons; i += 1) {
        this.addNewButtons(false);
      }
    }
    // if (this.schema.stepID) {
    //   this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
    //     this.$refs.buttonsObserver.validate();
    //   });
    // }
    setTimeout(() => {
      if (this.value) {
        // this.$refs.buttonsObserver.validate();
      }
    }, 0);

    this.$nextTick(() => {
      this.initialized = true;
    });
  },
  methods: {
    addNewButtons(focus = true) {
      // Do not add new buttons if the form is not valid or the max number of buttons is reached
      if (
        !this.valid ||
        this.noAddButton ||
        (this.maxButtons && this.buttons.length >= this.maxButtons)
      ) return;

      this.buttons.push({ value: '' });
      this.$nextTick(() => {
        const targetButton = `buttonInputRef-${this.buttons.length - 1}`;
        if (focus) this.$refs[targetButton][0].focus();
        this.emitData();
      });
    },
    removeOption(index) {
      this.buttons.splice(index, 1);
      this.emitData();
    },
    enterPressed(e) {
      if (e.target.value.length && this.enter && this.enter.toLowerCase() === 'add') {
        this.addNewButtons();
      }
    },
    setValue() {
      if (this.value) {
        const buttonFormat = this.value.map(button => ({
          value: button.title || button,
          ...(button.readOnly && { readOnly: button.readOnly }),
          ...(button.subtitlePrefixIcon && { subtitlePrefixIcon: button.subtitlePrefixIcon }),
          ...(button.subtitle && { subtitle: button.subtitle }),
        }));
        this.buttons = buttonFormat;
      } else {
        this.buttons = [];
      }
    },
    /**
     * Sets the data for the buttons from the outside component mostly for edit purposes
     * @param {Array} buttonList List of buttons to set. Each button should be a string. e.g. ['button1', 'button2', 'button3']
     */
    setData(buttonList) {
      this.buttons = Array.isArray(buttonList) ? buttonList.map(button => ({ value: button })) : [];
    },
    emitData() {
      this.$nextTick(() => {
        if (!this.initialized) return;
        if (!this.$refs.buttonsObserver) return;
        this.$refs.buttonsObserver.validate({ silent: true })
          .then((valid) => {
            this.valid = valid;
            const filteredValues = this.buttons.map(button => button.value); // .filter(v => v); // removes empty strings // Filtering empty strings will mess up the conversation component for borders on message. Filtering is done on the parent element instead.
            this.$emit('status', {
              valid,
            });
            this.$emit('input', filteredValues.length ? filteredValues : []);
          })
          .finally(() => {
          //   if (reset) this.$refs.buttonsObserver.reset();
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 2px;
  position: relative;
}

.subtitle {
  line-height: 1.4;
  margin-bottom: 8px;
}

.button-fields {
  margin: 0 0 8px 0 !important;

  &:last-child {
    margin-bottom: 0 !important;
  }

  .form-group {
    margin: 0 0 0 0 !important;
    padding: 0 0 0 0 !important;;
  }
}

.add-more-btn {
  display: inline-block;
  width: fit-content;
  color: $bluePrimary;
  padding: 0 0 0 0;
  // margin-top: 15px;

  &.black:not(.disabled) {
    color: $blackPrimary;
  }

  span {
    font-weight: 500;
    font-size: 14px;
  }

  &:hover {
    cursor: pointer;
    color: $blackPrimary;
    ::v-deep .sb-icon .mask {
      background-color: $blackPrimary !important;
    }
  }

  &.disabled {
    pointer-events: none;
    color: $greyLight !important;

    ::v-deep .icon.mask {
      background-color: $greyLight !important;
    }
  }
}

.button__label {
  margin-bottom: 5px !important;

  .character-counter {
    color: $blackUltralight;
  }
}

.option {
  position: relative;
  height: 34px;

  .option-item {
    margin-bottom: 10px;
    padding-right: 30px;
    border-radius: 10px !important;
  }

  .delete {
    position: absolute;
    top: 7px !important;
    right: 7px !important;
    margin-right: 0 !important;
    cursor: pointer;
  }

  &:hover {
    .delete {
      display: flex;
    }
  }
}

.error * {
  font-size: 12px !important;
  line-height: 1 !important;
}

.center {
  text-align: center;

  .button-fields input {
    text-align: center;
  }

  .add-more-btn {
    margin: 0 auto;
  }
}
</style>
